import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import pMap from 'p-map-browser';

import { getTextCardElements } from '../AccountCardEmptyTextAlertModal/helpers';
import Button from '../../../../../../../../../../../../components/global/Button';
import FormGroup from '../../../../../../../../../../../../components/global/FormGroup';
import withModal from '../../../../../../../../../../../../components/global/modal/withModal';
import { getAccountCardElementsConfiguration } from '../../../../../../../../../../../../services/cards/components/AccountCard/helpers';
import { getMatchingTextElements } from '../../../../../../../../../../../../services/cards/views/AccountCardConfigurator/helpers';
import { TEXT_PRESETS } from '../../../../../../../../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import { capitalize } from '../../../../../../../../../../../../util/text';

const propTypes = {
  className: PropTypes.string,
  actionProps: PropTypes.shape({
    accountCard: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    onComplete: PropTypes.func.isRequired,
  }),
  onRequestClose: PropTypes.func.isRequired,
};

const AccountCardEmptyTextFormModal = ({
  className,
  actionProps,
  onRequestClose,
}) => {
  const {
    accountCard,
    onElementConfigurationChange,
    onComplete,
    canGoBack = true,
    buttonText = 'Save and continue',
    title = 'Personalize your custom print by adding your info below:',
  } = actionProps ?? {};
  const [saving, setSaving] = useState(false);

  const classes = classNames({
    'account-card-empty-text-form-modal': true,
    [className]: !!className,
  });

  const missingElements = useMemo(
    () => getTextCardElements({ accountCard, emptyOnly: true }),
    [accountCard],
  );

  const cardElementConfigurations = useMemo(
    () => getAccountCardElementsConfiguration(accountCard),
    [accountCard],
  );

  const editableElements = useMemo(
    () =>
      missingElements
        .map(el =>
          cardElementConfigurations.find(e => e.cardElement.id === el.id),
        )
        .filter(Boolean)
        .sort((a, b) => b.cardElement.z_index - a.cardElement.z_index),
    [missingElements, cardElementConfigurations],
  );

  const groupedEditableElements = useMemo(() => {
    const groups = [];
    const seenCardElementIds = [];
    const editableCardElementIds = editableElements.map(e => e.cardElement.id);

    editableElements.forEach(element => {
      const { id } = element.cardElement;
      if (seenCardElementIds.includes(id)) {
        return;
      }

      const placeholder =
        element?.defaultCardElementConfiguration?.help_placeholder ??
        element?.cardElementConfiguration?.help_placeholder;

      const isLinkableTextElement =
        !!placeholder && !Object.values(TEXT_PRESETS).includes(placeholder);

      if (!isLinkableTextElement) {
        groups.push({ rep: element, cardElementIds: [id] });
        seenCardElementIds.push(id);
        return;
      }

      const matchedCardElementIds = getMatchingTextElements({
        element,
        accountCard,
      })
        .map(({ cardElement }) => cardElement.id)
        .filter(cardElementId =>
          editableCardElementIds.includes(cardElementId),
        );

      matchedCardElementIds.forEach(cardElementId =>
        seenCardElementIds.push(cardElementId),
      );
      groups.push({ root: element, cardElementIds: matchedCardElementIds });
    });

    return groups;
  }, [editableElements, accountCard]);

  const initialValues = useMemo(() => {
    const parsedValues = {};
    groupedEditableElements.forEach(({ root: element }) => {
      parsedValues[element.cardElement.id] = '';
    });
    return parsedValues;
  }, [groupedEditableElements]);

  const { values, handleChange, handleSubmit } = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async submittedValues => {
      if (saving) {
        return;
      }

      try {
        setSaving(true);
        await pMap(
          Object.entries(submittedValues),
          async ([cardElementId, textValue]) => {
            const group = groupedEditableElements.find(
              ({ root }) => root.cardElement.id === cardElementId,
            );
            const element = group?.root;
            if (!element || !textValue) {
              return;
            }

            await onElementConfigurationChange(element, { text: textValue });
          },
          { concurrency: 3 },
        );
        onComplete?.();
        onRequestClose();
      } finally {
        setSaving(false);
      }
    },
  });

  const hasEnteredText = useMemo(() => {
    return Object.values(values).some(
      value => (value ?? '').toString().trim().length > 0,
    );
  }, [values]);

  return (
    <div className={classes}>
      <h4 className="text-center">{title}</h4>

      <div className="account-card-empty-text-form-modal-fields">
        {groupedEditableElements.map(({ root: element }) => {
          const label = (
            element?.defaultCardElementConfiguration?.help_placeholder ??
            element?.cardElementConfiguration?.help_placeholder ??
            'Write your message here...'
          ).toUpperCase();
          const { id: repId } = element.cardElement;
          return (
            <FormGroup
              key={repId}
              id={repId}
              label={label}
              value={values[repId]}
              placeholder="Insert here..."
              onChange={e => {
                const { value } = e.target;
                if (!value || value?.[0] === value?.[0].toUpperCase()) {
                  handleChange({
                    target: { name: repId, value: e.target.value },
                  });
                } else {
                  handleChange({
                    target: { name: repId, value: capitalize(e.target.value) },
                  });
                }
              }}
            />
          );
        })}
      </div>

      <div className="text-center mt-2">
        <Button
          btnBlock
          onClick={handleSubmit}
          disabled={saving || !hasEnteredText}
        >
          {saving ? 'Saving...' : buttonText}
        </Button>
        {canGoBack && (
          <Button btnBlock btnStyle="link" onClick={onRequestClose}>
            &larr; Go Back
          </Button>
        )}
      </div>
    </div>
  );
};

AccountCardEmptyTextFormModal.propTypes = propTypes;

export const ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL =
  'ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL';

export default withModal(AccountCardEmptyTextFormModal, {
  name: ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL,
  dismissible: false,
  hideHeader: true,
  shouldCloseOnOverlayClick: false,
  type: 'full',
  size: 'sm',
});
